/*
 * sticky-add-to-cart.js — floating PDP bar.
 *
 * Mirrors the Purchase Form's ATC state machine. The Purchase Form
 * is the source of truth: it dispatches `pdp:state-change` with
 * `{ state, variant, productId }` whenever the resolved state
 * updates, and the sticky bar listens at the document level and
 * updates its button label, disabled state, hidden variant input,
 * data-atc-state attribute, and price block accordingly.
 *
 * Click routing (driven by `data-atc-state` on the button):
 *   atc / preorder → let the form submit (goes through
 *     sa-product-form → ajax-cart.js → cart drawer)
 *   oos            → button is disabled, no action
 *   enquiry        → preventDefault, dispatch `pdp:open-enquiry`
 *   notify         → preventDefault, dispatch `pdp:open-notify`
 *
 * Visibility:
 *   `body.sticky-atc--visible` is added when the main product
 *   info has scrolled past the viewport top. An
 *   IntersectionObserver on the footer toggles
 *   `body.footer--in-view` so the sticky bar slides out as the
 *   footer comes into view (CSS rule already in place).
 *
 * Loaded as `type="module"` in snippets/scripts.liquid so we can
 * pull H3.0's money formatter — the price block is re-rendered
 * client-side using the variant in the event payload, the shop's
 * money format string, and the active currency (both passed in
 * via data attributes on #stickyAtc).
 */

import { formatMoney } from '@theme/money-formatting';

function init() {
  const stickyAtc = document.getElementById('stickyAtc');
  if (!stickyAtc) return;

  const productMain = document.querySelector('.product-main');
  if (!productMain) return;

  const footer =
    document.querySelector('footer') ||
    document.querySelector('[class*="footer-group"]');

  const stickyVariantInput = document.getElementById('sticky-variant-id');
  const button = stickyAtc.querySelector('.sticky-atc__add-btn');
  const label = stickyAtc.querySelector('.sticky-atc__add-btn-label');
  const priceContainer = stickyAtc.querySelector('.sticky-atc__price');

  const moneyFormat = stickyAtc.dataset.moneyFormat || '${{amount}}';
  const currency = stickyAtc.dataset.currency || 'USD';

  // ---------- Visibility (scroll-based) -----------------------
  function checkStickyVisibility() {
    const mainBottom = productMain.getBoundingClientRect().bottom;
    const vh = window.innerHeight;
    if (mainBottom < vh) {
      document.body.classList.add('sticky-atc--visible');
    } else {
      document.body.classList.remove('sticky-atc--visible');
    }
  }
  window.addEventListener('scroll', checkStickyVisibility, { passive: true });
  window.addEventListener('resize', checkStickyVisibility);
  checkStickyVisibility();

  // ---------- Footer in-view (IntersectionObserver) -----------
  // `body.footer--in-view` is the override the CSS uses to slide
  // the sticky bar back out as the footer enters the viewport.
  // IO is more reliable than getBoundingClientRect polls and
  // doesn't compete with the scroll handler above.
  if (footer && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            document.body.classList.add('footer--in-view');
          } else {
            document.body.classList.remove('footer--in-view');
          }
        });
      },
      { threshold: 0 }
    );
    io.observe(footer);
  }

  // ---------- State mirror ------------------------------------
  // Apply a resolved state to the sticky bar button. Mirrors the
  // Purchase Form's _applyState shape so the two stay visually
  // consistent (same labels, same modifier classes).
  function applyState(state, variant) {
    if (!button) return;

    button.classList.remove(
      'is-sold-out',
      'is-preorder',
      'enquiry-mode',
      'bis-mode'
    );
    button.dataset.atcState = state;

    switch (state) {
      case 'enquiry':
        if (label) label.textContent = 'Make an Enquiry';
        button.type = 'button';
        button.disabled = false;
        button.classList.add('enquiry-mode');
        button.dataset.variantId = variant?.id ?? '';
        break;

      case 'notify':
        if (label) label.textContent = 'Notify Me When Available';
        button.type = 'button';
        button.disabled = false;
        button.classList.add('bis-mode');
        button.dataset.variantId = variant?.id ?? '';
        break;

      case 'oos':
        if (label) label.textContent = 'Out of Stock';
        button.type = 'button';
        button.disabled = true;
        button.classList.add('is-sold-out');
        break;

      case 'preorder':
        if (label) label.textContent = 'Pre-Order';
        button.type = 'submit';
        button.disabled = false;
        button.classList.add('is-preorder');
        break;

      case 'atc':
      default:
        if (label) label.textContent = 'Add to Cart';
        button.type = 'submit';
        button.disabled = false;
        break;
    }
  }

  // ---------- Price re-render ---------------------------------
  // sa-price snippet logic with is_pdp=true: shows EITHER the
  // sale price (with "Sale" tag + struck compare_at) OR the
  // regular price — never both. Keep the JS output in lockstep
  // with the Liquid so the markup looks identical pre- and
  // post-variant-change.
  function renderPrice(variant) {
    if (!priceContainer || !variant) return;
    const onSale =
      variant.compare_at_price &&
      Number(variant.compare_at_price) > Number(variant.price);

    const wrap = document.createElement('span');
    wrap.className = 'sa-price';

    if (onSale) {
      const tag = document.createElement('span');
      tag.className = 'product-main__tag';
      tag.textContent = 'Sale';
      wrap.appendChild(tag);

      const old = document.createElement('s');
      old.className = 'sa-price__old product-main__price--compare';
      old.dataset.comparePrice = '';
      old.textContent = formatMoney(variant.compare_at_price, moneyFormat, currency);
      wrap.appendChild(old);
    } else {
      const final = document.createElement('span');
      final.className = 'sa-price__final product-main__price';
      final.dataset.productPrice = '';
      final.textContent = formatMoney(variant.price, moneyFormat, currency);
      wrap.appendChild(final);
    }

    priceContainer.replaceChildren(wrap);
  }

  // ---------- pdp:state-change listener -----------------------
  // Document-level so it works regardless of whether the
  // Purchase Form is in the same section (PDP grid) or a sibling
  // section. Filter by productId to avoid cross-product bleed
  // when a future quick-add lands on the same page.
  document.addEventListener('pdp:state-change', (event) => {
    const detail = event.detail || {};
    const variant = detail.variant;
    if (!variant) return;

    if (stickyVariantInput) stickyVariantInput.value = variant.id;
    applyState(detail.state || (variant.available ? 'atc' : 'oos'), variant);
    renderPrice(variant);
  });

  // ---------- variant:update fallback -------------------------
  // When the PDP uses H3.0's native variant-picker block instead
  // of (or in addition to) the SA Purchase Form, the picker
  // dispatches `variant:update` directly without going through
  // our state machine. The sticky bar can still derive a basic
  // atc/oos state from `variant.available` so the label + price
  // don't go stale. If Purchase Form is also present, its
  // `pdp:state-change` fires AFTER variant:update in the same
  // `_setCurrentVariant` call — so the fuller state wins
  // naturally (last-write).
  document.addEventListener('variant:update', (event) => {
    const variant = event?.detail?.resource;
    if (!variant) return;

    if (stickyVariantInput) stickyVariantInput.value = variant.id;
    applyState(variant.available ? 'atc' : 'oos', variant);
    renderPrice(variant);
  });

  // ---------- Click routing -----------------------------------
  // Match the Purchase Form's _bindAtcStateClicks: enquiry/notify
  // states preventDefault + dispatch their modal-open event;
  // atc/preorder fall through to natural form submit; oos is
  // disabled so click won't fire anyway.
  if (button) {
    button.addEventListener('click', (event) => {
      const state = button.dataset.atcState;
      if (state !== 'enquiry' && state !== 'notify') return;
      event.preventDefault();

      const variantId = stickyVariantInput?.value;
      const name = state === 'enquiry' ? 'pdp:open-enquiry' : 'pdp:open-notify';
      button.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: {
            variant: variantId ? { id: Number(variantId) } : null,
          },
        })
      );
    });
  }

  // ---------- Initial variant id sync -------------------------
  // Seed the sticky bar's hidden input from the main product
  // form's id input immediately so the SSR markup is in sync
  // before any event arrives.
  const mainVariantInput = productMain.querySelector('input[name="id"]');
  if (mainVariantInput && stickyVariantInput) {
    stickyVariantInput.value = mainVariantInput.value;
  }

  // ---------- Late-subscriber handshake -----------------------
  // The Purchase Form dispatches its initial `pdp:state-change`
  // during construction (also at DCL), BEFORE the listener
  // above was attached — so the first paint of the sticky bar
  // is stuck on the SSR'd atc/oos state, missing Made-to-Order
  // (Enquiry), Pre-Order, and Notify Me. Pinging
  // `pdp:request-state` makes the Purchase Form re-broadcast
  // its current state so we catch up immediately. Fire on the
  // next microtask so all listeners on the page (this one
  // included) are guaranteed registered before the re-fire
  // arrives.
  queueMicrotask(() => {
    document.dispatchEvent(new CustomEvent('pdp:request-state'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
